import axios, { AxiosResponse } from 'axios'
import { saveAs } from 'file-saver'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { fetchChargingSessionReportFile } from '../api/export-charging-sessions.api'

import { ExportSessionsPayload } from '../models/charging-sessions-export.model'
import { exportChargingSessionsApiErrors } from '../api/dto/export-charging-sessions-api-errors'
import { ApiResult, usePortalMutation } from 'src/Libs/api'

interface UseExportChargingSessionHook {
  isLoading: boolean
  exportSessions: (payload: ExportSessionsPayload) => void
}

/**
 * Hook used to trigger the endpoint to fetch the charging session reports, convert them
 * to an actual file and start the download to the user's browser.
 */
/**
 * Function for add new tariff data
 * @param payload - data off ExportSessionsPayload added to mutate react-query function
 * @returns isLoading as boolean
 * @returns mutate function from react-query
 */
export const useExportChargingSessions = (): UseExportChargingSessionHook => {
  const { t } = useCustomTranslation()
  const { enqueueErrorStack } = useSnackbar()
  const authFetch = useAuthFetchWithResponse()

  const fetchFile = async (payload: ExportSessionsPayload): Promise<ApiResult<AxiosResponse<string>>> => {
    const response = await fetchChargingSessionReportFile({
      from: payload.dateFrom,
      to: payload.dateTo,
      installationId: payload.installationId,
      format: payload.exportFormat,
      wallboxIdFilter: payload.wallboxIdFilter,
      rfidFilter: payload.rfidFilter,
      midMeter: payload.midMeter,
      language: payload.language,
      reimbursementPrice: payload.reimbursementPrice,
      authFetch,
    })
    return new ApiResult(response)
  }

  const { isPending, mutate } = usePortalMutation<AxiosResponse<string>, Error, ExportSessionsPayload>({
    mutationFn: fetchFile,
    onError: async error => {
      if (axios.isAxiosError(error)) {
        const blob = error.response?.data

        if (blob instanceof Blob) {
          const text = await blob.text()
          const data = JSON.parse(text)
          enqueueErrorStack(t(exportChargingSessionsApiErrors[data.errorCode]))
        } else {
          enqueueErrorStack(t(exportChargingSessionsApiErrors[error.response?.data.errorCode]))
        }
      } else {
        enqueueErrorStack(t('emobility.chargingSession.export-failed'))
      }
    },
    onSuccess: response => {
      if (!response) return
      const responseFilename = response.headers['content-disposition']?.split('filename=')[1]
      const filename = responseFilename

      const blob = new Blob([response.data])
      saveAs(blob, filename)
    },
  })

  return {
    exportSessions: mutate,
    isLoading: isPending,
  }
}
